// === SAA:CLIENT-INFORMATION:VALIDATION ===
// The client-information form rules, shared by the screen (per-field + submit) and by the
// progress-step navigation (a forward click is only allowed while these still hold).

/** Validate initials format */
export const isValidInitials = (initials: string): boolean => {
  if (!initials || initials.trim().length === 0) return false;
  const trimmed = initials.trim();
  const formattedRegex = /^([A-Z]\.)+$/;
  const unformattedRegex = /^[a-zA-Z]+$/;
  return formattedRegex.test(trimmed) || unformattedRegex.test(trimmed);
};

/** Validate name (letters, spaces, hyphens, apostrophes) */
export const isValidName = (name: string): boolean => {
  if (!name || name.trim().length < 2) return false;
  const nameRegex = /^[a-zA-Z\u00C0-\u00FF\s'-]+$/;
  return nameRegex.test(name.trim());
};

/** Validate email format */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.trim().length === 0) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/** Validate phone number (7-15 digits per ITU-T E.164) */
export const isValidPhone = (phone: string): boolean => {
  if (!phone || phone.trim().length === 0) return false;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};

const DATE_OF_BIRTH_REGEX = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[012])-\d{4}$/;

/** Validate date of birth (DD-MM-YYYY, must be 18-120 years old) */
export const isValidDateOfBirth = (dateOfBirth: string): boolean => {
  if (!dateOfBirth || dateOfBirth.trim().length === 0) return false;

  if (!DATE_OF_BIRTH_REGEX.test(dateOfBirth)) return false;

  const parts = dateOfBirth.split('-');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);

  const date = new Date(year, month, day);

  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return false;
  }

  const today = new Date();
  const age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const dayDiff = today.getDate() - date.getDate();

  if (age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))) {
    return false;
  }

  if (age > 120) {
    return false;
  }

  return true;
};

export interface ClientInfoFields {
  initials?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
}

/** Submit-time validation: field path -> i18n error key (empty object = valid). */
export const validateClientInfo = (clientInfo: ClientInfoFields | null | undefined): Record<string, string> => {
  const errors: Record<string, string> = {};
  const ci = clientInfo || {};

  if (!ci.initials || ci.initials.trim().length === 0) {
    errors['clientInfo.initials'] = 'validation.initialsRequired';
  } else if (!isValidInitials(ci.initials)) {
    errors['clientInfo.initials'] = 'validation.initialsInvalid';
  }

  if (!ci.lastName || ci.lastName.trim().length === 0) {
    errors['clientInfo.lastName'] = 'validation.lastNameRequired';
  } else if (ci.lastName.trim().length < 2) {
    errors['clientInfo.lastName'] = 'validation.lastNameTooShort';
  } else if (!isValidName(ci.lastName)) {
    errors['clientInfo.lastName'] = 'validation.lastNameInvalid';
  }

  if (!ci.email || ci.email.trim().length === 0) {
    errors['clientInfo.email'] = 'validation.emailRequired';
  } else if (!isValidEmail(ci.email)) {
    errors['clientInfo.email'] = 'validation.emailInvalid';
  }

  if (!ci.phone || ci.phone.trim().length === 0) {
    errors['clientInfo.phone'] = 'validation.phoneRequired';
  } else if (!isValidPhone(ci.phone)) {
    errors['clientInfo.phone'] = 'validation.phoneInvalid';
  }

  if (!ci.dateOfBirth || ci.dateOfBirth.trim().length === 0) {
    errors['clientInfo.dateOfBirth'] = 'validation.dateOfBirthRequired';
  } else if (!isValidDateOfBirth(ci.dateOfBirth)) {
    // Check specific reason for better error message
    if (!DATE_OF_BIRTH_REGEX.test(ci.dateOfBirth)) {
      errors['clientInfo.dateOfBirth'] = 'validation.dateOfBirthInvalid';
    } else {
      // Date format is correct but date itself is invalid
      const parts = ci.dateOfBirth.split('-');
      const year = parseInt(parts[2], 10);
      const date = new Date(year, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
      const age = new Date().getFullYear() - date.getFullYear();

      if (age < 18) {
        errors['clientInfo.dateOfBirth'] = 'validation.dateOfBirthTooYoung';
      } else if (age > 120) {
        errors['clientInfo.dateOfBirth'] = 'validation.dateOfBirthTooOld';
      } else {
        errors['clientInfo.dateOfBirth'] = 'validation.dateOfBirthInvalid';
      }
    }
  }

  if (!ci.gender || ci.gender.trim().length === 0) {
    errors['clientInfo.gender'] = 'validation.genderRequired';
  }

  return errors;
};

export const isClientInfoComplete = (clientInfo: ClientInfoFields | null | undefined): boolean =>
  Object.keys(validateClientInfo(clientInfo)).length === 0;
